import { useEffect, useMemo, useState } from "react";
import { Button, Empty, InputNumber, Pagination, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import PageLayout from "@layouts/PageLayout";
import { formatInr } from "@services/shop.service";
import { changeCartItem, deleteCartItem, fetchCart } from "@store/cartSlice";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { PAGE_SIZE } from "./list";
import "./shop.css";

const fallbackImage = "https://placehold.co/200x200?text=Item";

function CartPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cart = useAppSelector((state) => state.cart);
  const token = useAppSelector((state) => state.auth.token);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  const items = cart.items || [];
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = useMemo(() => {
    const start = (current - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, current]);

  return (
    <PageLayout>
      <div className="shop-page">
        <div className="shop-hero">
          <div>
            <h2>Cart</h2>
            <p>
              {token
                ? `${cart.itemCount} item${cart.itemCount === 1 ? "" : "s"}`
                : `${cart.itemCount} item${cart.itemCount === 1 ? "" : "s"} saved as a guest. Sign in to pay.`}
            </p>
          </div>
          <Button
            type="primary"
            disabled={!items.length || cart.status === "loading"}
            onClick={() => navigate(token ? "/checkout" : "/login?next=/checkout")}
          >
            {token ? `Checkout ${formatInr(cart.subtotal)}` : `Sign in to pay ${formatInr(cart.subtotal)}`}
          </Button>
        </div>
        {cart.status === "loading" && !items.length ? (
          <div className="surface" style={{ textAlign: "center", padding: 48 }}><Spin /></div>
        ) : !items.length ? (
          <div className="surface">
            <Empty description="Your cart is empty">
              <Button type="primary" onClick={() => navigate("/")}>Continue shopping</Button>
            </Empty>
          </div>
        ) : (
          <div className="cart-panel">
            {visible.map((item) => (
              <div className="cart-line" key={item.id}>
                <img
                  src={item.product?.image_url || fallbackImage}
                  alt=""
                  onError={(event) => { event.currentTarget.src = fallbackImage; }}
                />
                <div>
                  <strong>{item.product?.name || "Unavailable item"}</strong>
                  <div className="muted">{formatInr(item.product?.price || 0)}</div>
                  <InputNumber
                    min={1}
                    max={Math.max(1, item.product?.stock || 1)}
                    value={item.quantity}
                    onChange={(value) =>
                      dispatch(changeCartItem({ id: item.id, quantity: Number(value) || 1 }))
                    }
                  />
                </div>
                <div className="line-total">
                  <div>{formatInr(item.line_total)}</div>
                  <Button type="link" danger onClick={() => dispatch(deleteCartItem(item.id))}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div className="list-footer" style={{ padding: "8px 16px 16px" }}>
              <Pagination
                current={current}
                pageSize={PAGE_SIZE}
                total={items.length}
                hideOnSinglePage
                showSizeChanger={false}
                onChange={setPage}
              />
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

export default CartPage;
